import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Trophy,
  CalendarDays,
  ShoppingBag,
  ListChecks,
  UserPlus,
  Users,
  FilePenLine,
  Settings,
  ArrowRight,
  Tags,
  BarChart3,
  Building2,
} from 'lucide-react';

const adminLinks = [
  {
    title: 'Gérer les tournois',
    description: 'Créer, modifier, masquer, archiver et gérer les images des tournois.',
    to: '/admin/tournois',
    icon: Trophy,
    color: 'from-[#4c2f8e] to-[#ea4c89]',
    badge: 'Tournois',
  },
  {
    title: 'Gérer les stages',
    description: 'Créer, modifier, masquer, archiver et gérer les stages.',
    to: '/admin/stage',
    icon: CalendarDays,
    color: 'from-blue-600 to-cyan-500',
    badge: 'Stages',
  },
  {
    title: 'Gérer les ligues',
    description: 'Créer, modifier, masquer, archiver et gérer les ligues.',
    to: '/admin/leagues',
    icon: ListChecks,
    color: 'from-indigo-600 to-purple-500',
    badge: 'Ligues',
  },
  {
    title: 'Gérer la boutique',
    description: 'Gérer les produits, catégories, prix, tailles, promotions, stock et images.',
    to: '/admin/shop',
    icon: ShoppingBag,
    color: 'from-pink-600 to-rose-500',
    badge: 'Boutique',
  },
  {
    title: 'Gérer les Team Building',
    description:
      'Créer, modifier, masquer et archiver les événements Team Building pour les entreprises.',
    to: '/admin/team-building',
    icon: Building2,
    color: 'from-violet-600 to-fuchsia-500',
    badge: 'Team Building',
  },
  {
    title: 'Ajouter une équipe à un tournoi',
    description: 'Ajouter manuellement une équipe dans un tournoi existant.',
    to: '/admin/add-team',
    icon: UserPlus,
    color: 'from-green-600 to-emerald-500',
    badge: 'Ajout équipe',
  },
  {
    title: 'Gérer les joueurs',
    description: 'Rechercher, modifier et gérer la base globale des joueurs.',
    to: '/admin/AdminJoueurs',
    icon: Users,
    color: 'from-orange-500 to-amber-500',
    badge: 'Joueurs',
  },
  {
    title: 'Gérer les inscriptions tournoi',
    description: 'Modifier les équipes inscrites : nom d’équipe, joueurs, email, téléphone, ELO, paiement.',
    to: '/admin/tournament-inscriptions',
    icon: FilePenLine,
    color: 'from-gray-900 to-gray-600',
    badge: 'Inscriptions',
  },
  {
    title: 'Gérer le planning des équipes',
    description: 'Accéder au planning des équipes, matchs, horaires et organisation des rencontres.',
    to: '/planning',
    icon: CalendarDays,
    color: 'from-teal-600 to-cyan-500',
    badge: 'Planning',
  },
  {
    title: 'Codes promo',
    description: 'Créer et gérer les codes promo utilisés sur le site.',
    to: '/admin/codepromo',
    icon: Tags,
    color: 'from-purple-700 to-fuchsia-500',
    badge: 'Promos',
  },
];

const AdminMenu = () => {
  const navigate = useNavigate();
  const [eloTournamentId, setEloTournamentId] = useState('');

  const handleOpenEloMenu = (e) => {
    e.preventDefault();

    const id = eloTournamentId.trim();

    if (!id) {
      alert('Entre un ID de tournoi.');
      return;
    }

    navigate(`/tournament/${encodeURIComponent(id)}/viewadminelo`); };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="max-w-7xl mx-auto">
        <div className="mb-10 bg-white border border-gray-200 rounded-3xl p-6 md:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-[#4c2f8e] to-[#ea4c89] flex items-center justify-center shadow-lg">
                <Settings className="w-7 h-7 text-white" />
              </div>

              <div>
                <h1 className="text-3xl md:text-4xl font-black text-[#4c2f8e] leading-tight">
                  Administration Padel Mendyx
                </h1>

                <p className="text-gray-600 mt-2 max-w-3xl">
                  Menu central pour gérer les tournois, stages, ligues, boutique, joueurs, équipes,
                  inscriptions, codes promo et ELO.
                </p>
              </div>
            </div>

            <div className="bg-purple-50 border border-purple-200 text-purple-800 rounded-2xl px-4 py-3 text-sm">
              Point d’entrée actuel : <strong>/admin/menu</strong>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {adminLinks.map((item) => {
            const Icon = item.icon;

            return (
              <Link
                key={item.to}
                to={item.to}
                className="group bg-white border border-gray-200 rounded-3xl p-5 shadow-sm hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div
                    className={`w-14 h-14 rounded-2xl bg-gradient-to-r ${item.color} flex items-center justify-center shadow-lg`}
                  >
                    <Icon className="w-7 h-7 text-white" />
                  </div>

                  <span className="text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full bg-gray-100 text-gray-600">
                    {item.badge}
                  </span>
                </div>

                <h2 className="text-xl font-extrabold text-gray-900 group-hover:text-[#4c2f8e] transition">
                  {item.title}
                </h2>

                <p className="text-sm text-gray-600 mt-2 leading-relaxed min-h-[44px]">
                  {item.description}
                </p>

                <div className="mt-5 flex items-center gap-2 text-sm font-bold text-[#ea4c89]">
                  Ouvrir
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            );
          })}

          <form
            onSubmit={handleOpenEloMenu}
            className="group bg-white border border-gray-200 rounded-3xl p-5 shadow-sm hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
          >
            <div className="flex items-start justify-between gap-4 mb-5">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center shadow-lg">
                <BarChart3 className="w-7 h-7 text-white" />
              </div>

              <span className="text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full bg-gray-100 text-gray-600">
                ELO
              </span>
            </div>

            <h2 className="text-xl font-extrabold text-gray-900 group-hover:text-[#4c2f8e] transition">
              Menu ELO tournoi
            </h2>

            <p className="text-sm text-gray-600 mt-2 leading-relaxed min-h-[44px]">
              Entrer l’ID d’un tournoi pour accéder directement à la page de gestion ELO.
            </p>

            <input
              type="text"
              value={eloTournamentId}
              onChange={(e) => setEloTournamentId(e.target.value)}
              placeholder="Exemple : 300"
              className="mt-4 w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#ea4c89]"
            />

            <button
              type="submit"
              className="mt-5 flex items-center gap-2 text-sm font-bold text-[#ea4c89]"
            >
              Ouvrir le menu ELO
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </form>
        </div>

        <div className="mt-10 p-4 rounded-2xl bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm">
          Conseil : tu peux garder <strong>/admin/menu</strong>, ou ajouter aussi une route{' '}
          <strong>/admin</strong> qui pointe vers ce même menu.
        </div>
      </div>
    </div>
  );
};

export default AdminMenu;